import { ReactElement, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { TextField } from '../../../components/TextField';
import { SelectField } from '../../../components/SelectField/SelectField';
import { Button } from '../../../components/Button/Button';
import { Alert } from '../../../components/Alert/Alert';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { FocusPageLayout } from '../../../components/FocusPageLayout';
import { Paragraph } from '../../../components/Paragraph/Paragraph';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { WalletIcon } from '@navikt/aksel-icons';
import classes from './EditLeadPage.module.css';
import { Routes } from '../../../enums/Routes';
import { useGetLeadDetail, useUpdateLead } from '../../../hooks/leads/useLeads';
import { useGetPartnerDashboardQuery } from '../../../hooks/queries/useGetPartnerDashboardQuery';
import { formatNOK } from '../../../utils/currencyFormatters';
import { parseOptionalNumber, convertNOKToOre, calculateCashbackEstimate } from '../../../utils/pricing';

// Schema will use dynamic translation in component
const createEditLeadSchema = (t: any) => z.object({
  companyName: z.string().min(1, t('partnerAdmin.editLead.validation.companyNameRequired')),
  contactPersonName: z.string().optional(),
  contactEmail: z.string().email(t('partnerAdmin.editLead.validation.invalidEmail')).optional().or(z.literal('')),
  contactPhone: z.string().optional(),
  source: z.string().optional(),
  quality: z.enum(['', 'HOT', 'WARM', 'COLD']).optional(),
  estimatedSeats: z.string().optional(),
  estimatedPricePerSeat: z.string().optional(),
  estimatedBillingPeriod: z.enum(['', 'monthly', 'yearly']).optional(),
  nextFollowUpDate: z.string().optional(),
  notes: z.string().optional(),
});

type EditLeadFormData = z.infer<ReturnType<typeof createEditLeadSchema>>;

export const EditLeadPage = (): ReactElement => {
  const { t } = useTranslation();
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const editLeadSchema = createEditLeadSchema(t);
  const { data: lead, isLoading, error } = useGetLeadDetail(Number(leadId));
  const updateLeadMutation = useUpdateLead();
  const { data: dashboardData } = useGetPartnerDashboardQuery();

  const [formData, setFormData] = useState<EditLeadFormData>({
    companyName: '',
    contactPersonName: '',
    contactEmail: '',
    contactPhone: '',
    source: '',
    quality: '',
    estimatedSeats: '',
    estimatedPricePerSeat: '',
    estimatedBillingPeriod: '',
    nextFollowUpDate: '',
    notes: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof EditLeadFormData, string>>>({});

  useEffect(() => {
    if (lead) {
      setFormData({
        companyName: lead.companyName,
        contactPersonName: lead.contactPersonName ?? '',
        contactEmail: lead.contactEmail ?? '',
        contactPhone: lead.contactPhone ?? '',
        source: lead.source ?? '',
        quality: lead.quality ?? '',
        estimatedSeats: lead.estimatedSeats?.toString() ?? '',
        estimatedPricePerSeat: lead.estimatedPricePerSeat ? (lead.estimatedPricePerSeat / 100).toString() : '',
        estimatedBillingPeriod: lead.estimatedBillingPeriod ?? '',
        nextFollowUpDate: lead.nextFollowUpDate ? lead.nextFollowUpDate.split('T')[0] : '',
        notes: lead.notes ?? '',
      });
    }
  }, [lead]);

  const cashbackEstimate = useMemo(
    () =>
      calculateCashbackEstimate(
        parseOptionalNumber(formData.estimatedPricePerSeat),
        parseOptionalNumber(formData.estimatedSeats),
        formData.estimatedBillingPeriod || null,
        dashboardData?.partnerInfo.cashbackPercentage ?? null,
      ),
    [formData.estimatedPricePerSeat, formData.estimatedSeats, formData.estimatedBillingPeriod, dashboardData],
  );

  const monthlyRevenue = useMemo(() => {
    const pricePerSeat = parseOptionalNumber(formData.estimatedPricePerSeat);
    const seats = parseOptionalNumber(formData.estimatedSeats);
    return pricePerSeat && seats ? pricePerSeat * seats : null;
  }, [formData.estimatedPricePerSeat, formData.estimatedSeats]);

  const isValidSeatsInput = (value: string) => !value || /^\d+$/.test(value);
  const isValidPriceInput = (value: string) => !value || /^\d+(\.\d{0,2})?$/.test(value);

  const updateField = (field: keyof EditLeadFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;

    if (field === 'estimatedSeats' && !isValidSeatsInput(value)) return;
    if (field === 'estimatedPricePerSeat' && !isValidPriceInput(value)) return;

    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (updateLeadMutation.isPending) return;

    const result = editLeadSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof EditLeadFormData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof EditLeadFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    const pricePerSeatNOK = parseOptionalNumber(formData.estimatedPricePerSeat);
    const pricePerSeatOre = pricePerSeatNOK !== null ? convertNOKToOre(pricePerSeatNOK) : undefined;

    // Build payload with only defined values
    const payload: any = {
      companyName: result.data.companyName,
    };

    if (result.data.contactPersonName?.trim()) payload.contactPersonName = result.data.contactPersonName.trim();
    if (result.data.contactEmail?.trim()) payload.contactEmail = result.data.contactEmail.trim();
    if (result.data.contactPhone?.trim()) payload.contactPhone = result.data.contactPhone.trim();
    if (result.data.source?.trim()) payload.source = result.data.source.trim();
    if (result.data.quality && result.data.quality !== '') payload.quality = result.data.quality;
    if (parseOptionalNumber(result.data.estimatedSeats) !== null) {
      payload.estimatedSeats = parseOptionalNumber(result.data.estimatedSeats);
    }
    if (pricePerSeatOre !== undefined) payload.estimatedPricePerSeat = pricePerSeatOre;
    if (result.data.estimatedBillingPeriod && result.data.estimatedBillingPeriod !== '') {
      payload.estimatedBillingPeriod = result.data.estimatedBillingPeriod;
    }
    if (result.data.nextFollowUpDate?.trim()) {
      payload.nextFollowUpDate = new Date(result.data.nextFollowUpDate).toISOString();
    }
    if (result.data.notes?.trim()) payload.notes = result.data.notes.trim();

    updateLeadMutation.mutate(
      {
        leadId: Number(leadId),
        payload,
      },
      {
        onSuccess: () => {
          toast.success(t('partnerAdmin.editLead.toast.updateSuccess'));
          navigate(`/partner-admin/leads/${leadId}`);
        },
        onError: () => {
          toast.error(t('partnerAdmin.editLead.toast.updateError'));
        },
      }
    );
  };

  if (isLoading) {
    return (
      <FocusPageLayout
        title={t('partnerAdmin.editLead.pageTitle')}
        backLink={Routes.PartnerAdminLeads}
        backLinkLabel={t('partnerAdmin.editLead.backToLeads')}
      >
        <GhostLoader />
      </FocusPageLayout>
    );
  }

  if (error || !lead) {
    return (
      <FocusPageLayout
        title={t('partnerAdmin.editLead.pageTitle')}
        backLink={Routes.PartnerAdminLeads}
        backLinkLabel={t('partnerAdmin.editLead.backToLeads')}
      >
        <Alert type="error" title={t('partnerAdmin.editLead.errors.loadError.title')}>
          {error ? t('partnerAdmin.editLead.errors.loadError.couldNotLoad') : t('partnerAdmin.editLead.errors.loadError.notFound')}
        </Alert>
      </FocusPageLayout>
    );
  }

  if (lead.convertedToTenantId !== null) {
    return (
      <FocusPageLayout
        title={t('partnerAdmin.editLead.pageTitle')}
        backLink={`/partner-admin/leads/${leadId}`}
        backLinkLabel={t('partnerAdmin.editLead.backToLead')}
      >
        <Alert type="warning" title={t('partnerAdmin.editLead.errors.convertedLead.title')}>
          {t('partnerAdmin.editLead.errors.convertedLead.message')}
        </Alert>
      </FocusPageLayout>
    );
  }

  return (
    <FocusPageLayout
      title={t('partnerAdmin.editLead.pageTitleWithCompany', { companyName: lead.companyName })}
      backLink={`/partner-admin/leads/${leadId}`}
      backLinkLabel={t('partnerAdmin.editLead.backToLead')}
    >
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('partnerAdmin.editLead.sections.companyInfo')}
          </Paragraph>

          <TextField
            name="companyName"
            label={t('partnerAdmin.editLead.fields.companyName.label')}
            value={formData.companyName}
            onChange={updateField('companyName')}
            placeholder={t('partnerAdmin.editLead.fields.companyName.placeholder')}
            type="text"
            required
            errorMessage={errors.companyName}
          />

          <TextField
            name="source"
            label={t('partnerAdmin.editLead.fields.source.label')}
            value={formData.source}
            onChange={updateField('source')}
            placeholder={t('partnerAdmin.editLead.fields.source.placeholder')}
            type="text"
            errorMessage={errors.source}
          />

          <SelectField
            name="quality"
            label={t('partnerAdmin.editLead.fields.leadQuality.label')}
            value={formData.quality}
            onChange={updateField('quality')}
            options={[
              { label: t('partnerAdmin.editLead.fields.leadQuality.options.hot'), value: 'HOT' },
              { label: t('partnerAdmin.editLead.fields.leadQuality.options.warm'), value: 'WARM' },
              { label: t('partnerAdmin.editLead.fields.leadQuality.options.cold'), value: 'COLD' },
            ]}
            placeholder={t('partnerAdmin.editLead.fields.leadQuality.placeholder')}
            errorMessage={errors.quality}
          />
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('partnerAdmin.editLead.sections.contactPerson')}
          </Paragraph>

          <TextField
            name="contactPersonName"
            label={t('partnerAdmin.editLead.fields.contactName.label')}
            value={formData.contactPersonName}
            onChange={updateField('contactPersonName')}
            placeholder={t('partnerAdmin.editLead.fields.contactName.placeholder')}
            type="text"
            errorMessage={errors.contactPersonName}
          />

          <div className={classes.nameFields}>
            <TextField
              name="contactEmail"
              label={t('partnerAdmin.editLead.fields.contactEmail.label')}
              value={formData.contactEmail}
              onChange={updateField('contactEmail')}
              placeholder={t('partnerAdmin.editLead.fields.contactEmail.placeholder')}
              type="email"
              errorMessage={errors.contactEmail}
            />

            <TextField
              name="contactPhone"
              label={t('partnerAdmin.editLead.fields.contactPhone.label')}
              value={formData.contactPhone}
              onChange={updateField('contactPhone')}
              placeholder={t('partnerAdmin.editLead.fields.contactPhone.placeholder')}
              type="text"
              errorMessage={errors.contactPhone}
            />
          </div>
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('partnerAdmin.editLead.sections.estimatedValue')}
          </Paragraph>
          <Paragraph size="sm" className={classes.sectionDescription}>
            {t('partnerAdmin.editLead.estimatedValueDescription')}
          </Paragraph>

          <TextField
            name="estimatedSeats"
            label={t('partnerAdmin.editLead.fields.estimatedSeats.label')}
            value={formData.estimatedSeats}
            onChange={updateField('estimatedSeats')}
            placeholder={t('partnerAdmin.editLead.fields.estimatedSeats.placeholder')}
            type="text"
            errorMessage={errors.estimatedSeats}
          />

          <TextField
            name="estimatedPricePerSeat"
            label={t('partnerAdmin.editLead.fields.pricePerSeat.label')}
            value={formData.estimatedPricePerSeat}
            onChange={updateField('estimatedPricePerSeat')}
            placeholder={t('partnerAdmin.editLead.fields.pricePerSeat.placeholder')}
            type="text"
            errorMessage={errors.estimatedPricePerSeat}
          />

          <SelectField
            name="estimatedBillingPeriod"
            label={t('partnerAdmin.editLead.fields.billingPeriod.label')}
            value={formData.estimatedBillingPeriod}
            onChange={updateField('estimatedBillingPeriod')}
            options={[
              { label: t('partnerAdmin.editLead.fields.billingPeriod.options.monthly'), value: 'monthly' },
              { label: t('partnerAdmin.editLead.fields.billingPeriod.options.yearly'), value: 'yearly' },
            ]}
            placeholder={t('partnerAdmin.editLead.fields.billingPeriod.placeholder')}
            errorMessage={errors.estimatedBillingPeriod}
          />

          {cashbackEstimate && (
            <ContainerCard>
              <ContainerCard.Title icon={<WalletIcon />}>
                {t('partnerAdmin.editLead.pipelineValue.title')}
              </ContainerCard.Title>
              <ContainerCard.Body>
                <Paragraph size="sm" style={{ marginBottom: '1rem', color: 'var(--a-text-subtle)' }}>
                  {t('partnerAdmin.editLead.pipelineValue.description')}
                </Paragraph>
                <div className={classes.cashbackBreakdown}>
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>{t('partnerAdmin.editLead.pipelineValue.pricePerSeat')}</span>
                    <span className={classes.breakdownValue}>
                      {formData.estimatedPricePerSeat ? `${formData.estimatedPricePerSeat} kr` : '–'}
                    </span>
                  </div>
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>{t('partnerAdmin.editLead.pipelineValue.numberOfSeats')}</span>
                    <span className={classes.breakdownValue}>
                      {formData.estimatedSeats || '–'}
                    </span>
                  </div>

                  <div className={classes.breakdownDivider} />

                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}><strong>{t('partnerAdmin.editLead.pipelineValue.monthlyValue')}</strong></span>
                    <span className={classes.breakdownValue}>
                      <strong>{monthlyRevenue ? formatNOK(monthlyRevenue) : '–'}</strong>
                    </span>
                  </div>

                  <div className={classes.breakdownHighlight}>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>{t('partnerAdmin.editLead.pipelineValue.estimatedMonthlyCashback')}</span>
                      <span className={classes.breakdownValue}>{formatNOK(cashbackEstimate.monthly)}</span>
                    </div>
                  </div>

                  <div className={classes.breakdownHighlight}>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>{t('partnerAdmin.editLead.pipelineValue.estimatedYearlyCashback')}</span>
                      <span className={classes.breakdownValue}>{formatNOK(cashbackEstimate.yearly)}</span>
                    </div>
                  </div>
                </div>
              </ContainerCard.Body>
            </ContainerCard>
          )}
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('partnerAdmin.editLead.sections.followUp')}
          </Paragraph>

          <TextField
            name="nextFollowUpDate"
            label={t('partnerAdmin.editLead.fields.nextFollowUpDate.label')}
            value={formData.nextFollowUpDate}
            onChange={updateField('nextFollowUpDate')}
            type="date"
            errorMessage={errors.nextFollowUpDate}
          />

          <div>
            <label htmlFor="notes" className={classes.notesLabel}>
              {t('partnerAdmin.editLead.fields.notes.label')}
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={updateField('notes')}
              placeholder={t('partnerAdmin.editLead.fields.notes.placeholder')}
              className={classes.notesField}
            />
            {errors.notes && <span className={classes.errorMessage}>{errors.notes}</span>}
          </div>
        </div>

        <div className={classes.actions}>
          <Button
            variant="secondary"
            size="medium"
            onClick={() => navigate(`/partner-admin/leads/${leadId}`)}
            disabled={updateLeadMutation.isPending}
            type="button"
          >
            {t('partnerAdmin.editLead.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            size="medium"
            type="submit"
            disabled={updateLeadMutation.isPending}
          >
            {updateLeadMutation.isPending ? t('partnerAdmin.editLead.actions.saving') : t('partnerAdmin.editLead.actions.save')}
          </Button>
        </div>
      </form>
    </FocusPageLayout>
  );
};
